import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { ApolloClient } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { deleteCookie, getCookie } from 'cookies-next'
import noop from 'lodash/noop'
import { v4 as uuid } from 'uuid'
import { paths } from '@/constants/paths'
import { PIF_NAME_IN_CREDITS_MIN_TICKETS } from '@/constants/pif'
import { generateRecaptchaToken } from '@/services/Captcha/Captcha'
import {
  GET_SAVED_PAYMENT_INFO_QUERY,
  GUEST_STRIPE_PURCHASE_MUTATION,
  GuestPurchaseRequest,
  PurchaseRequest,
  STRIPE_ONE_TIME_PURCHASE_MUTATION,
} from '@/services/PifCheckoutService'
import { getPeopleImpacted, Offer } from '@/services/PifService'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { User } from '@/services/UserService'
import {
  DoStripeMonthlySubscriptionMutation,
  DoStripeOneTimeGuestCheckoutMutation,
  DoStripeOneTimePurchaseMutation,
  GetSavedPaymentInfoQuery,
  MoneyCurrencyCode,
} from '@/types/codegen-federation'
import { SubmitNameForCreditsMutation, SubmitNameForCreditsMutationVariables } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { getFormErrorMessage } from '@/utils/StripeFormValidator'
import { useSafeTrack } from '@/utils/analytics'
import { OrderCompletedPayload, useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { logger } from '@/utils/logging'
import { Maybe } from '@/utils/types'
import { getUserDisplayName } from '@/utils/users'
import { SUBMIT_NAME_FOR_CREDITS } from '@/views/PreSales/PreSalesOrderView/components/NameInCreditsFormModal'
import { getPaymentMethod } from './PayItForwardHelper'
import { PaymentMethod } from './PayItForwardHelper/PayItForwardHelper'

interface PayItForwardFormProps {
  onComplete: (props: OrderCompleteArgs) => void
  offer: Offer
  projectSlug: Maybe<string>
  bimClient?: ApolloClient
  user?: User | null
  guildMembershipIncluded?: boolean
  useSavedPaymentMethod?: boolean
  nameInCreditsRequested?: string
}

interface PayItForwardFormState {
  name: string
  setName: (name: string) => void
  email: string
  setEmail: (email: string) => void
  errorMessage?: string
  isSubmitting: boolean
  savedPaymentInfo?: GetSavedPaymentInfoQuery['paymentInfo']
  setSavedPaymentInfo: (paymentInfo?: GetSavedPaymentInfoQuery['paymentInfo']) => void
  checkout: (e: FormEvent<HTMLFormElement>) => Promise<void>
  setSubmitting: (isSubmitting: boolean) => void
  onPayItForwardComplete: (args: OrderCompleteArgs) => void
  setErrorMessage: (error: string) => void
  bimClient?: ApolloClient
  guildMembershipIncluded?: boolean
  nameInCreditsRequested?: string
}

interface HandleErrorProps {
  checkoutId: string
  errorMessage?: string
}

export interface OrderCompleteArgs {
  orderId: string
  paymentMethod?: string | null
  token?: string | null
  email?: string | null
}

const PayItForwardFormContext = React.createContext<PayItForwardFormState>({
  name: '',
  setName: noop,
  email: '',
  setEmail: noop,
  errorMessage: undefined,
  isSubmitting: false,
  setSubmitting: noop,
  savedPaymentInfo: null,
  setSavedPaymentInfo: noop,
  checkout: () => Promise.resolve(),
  onPayItForwardComplete: noop,
  setErrorMessage: noop,
  bimClient: undefined,
  guildMembershipIncluded: false,
})

export const PayItForwardFormProvider: ReactFCC<PayItForwardFormProps> = ({
  children,
  onComplete,
  offer,
  projectSlug,
  user,
  bimClient,
  guildMembershipIncluded = false,
  useSavedPaymentMethod = true,
  nameInCreditsRequested,
}) => {
  const track = useSafeTrack()
  const { trackOrderCompleted: trackOrderCompletedEvent } = useEcommerceEvents()
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setSubmitting] = useState(false)
  const [savedPaymentInfo, setSavedPaymentInfo] = useState<GetSavedPaymentInfoQuery['paymentInfo']>()
  const [guestPurchaseMutation] = useMutation<DoStripeOneTimePurchaseMutation>(GUEST_STRIPE_PURCHASE_MUTATION, {
    client: bimClient,
    errorPolicy: 'all',
  })
  const [purchaseMutation] = useMutation<DoStripeOneTimePurchaseMutation>(STRIPE_ONE_TIME_PURCHASE_MUTATION, {
    client: bimClient,
    errorPolicy: 'all',
  })
  const [submitNameForCredits] = useMutation<SubmitNameForCreditsMutation, SubmitNameForCreditsMutationVariables>(
    SUBMIT_NAME_FOR_CREDITS,
    { client: bimClient, errorPolicy: 'all' },
  )
  const { data: savedPaymentData } = useQuery<GetSavedPaymentInfoQuery>(GET_SAVED_PAYMENT_INFO_QUERY, {
    skip: !user,
    errorPolicy: 'all',
  })

  // Basic sanitization for requested credit names
  const sanitizeCreditName = useCallback((name?: string): string => {
    if (!name) return ''
    // Trim whitespace; length is validated at submission time
    const trimmed = name.trim()
    return trimmed
  }, [])

  useEffect(() => {
    setName(getUserDisplayName(user))
    setEmail(user?.email ?? '')
  }, [user])

  useEffect(() => {
    if (savedPaymentData?.paymentInfo && useSavedPaymentMethod) {
      setSavedPaymentInfo(savedPaymentData?.paymentInfo)
    }
  }, [savedPaymentData, useSavedPaymentMethod])

  const handleError = useCallback(
    ({ checkoutId, errorMessage = 'Please check your payment method.' }: HandleErrorProps) => {
      setErrorMessage(errorMessage)
      setSubmitting(false)
      const trackingPayload = {
        category: 'pif',
        currency: offer.currency,
        region: offer.region,
        project: projectSlug,
        products: [
          {
            product_id: offer.id,
            sku: offer.slug,
            category: 'pif',
            name: offer.name,
            price: offer.price,
            currency: offer.currency,
            region: offer.region,
            quantity: 1,
            url: `${paths.getBaseURL()}${paths.payItForward.index}/${projectSlug}/checkout/${offer.id}`,
          },
        ],
        subscription: false,
        checkout_id: checkoutId,
      }
      track('Checkout Failed', trackingPayload)
      const err = new Error(errorMessage)
      logger().error('An error occurred during PIF checkout', { trackingPayload }, err)
    },
    [offer.currency, offer.id, offer.name, offer.price, offer.slug, offer.region, projectSlug, track],
  )

  const prepareForCheckout = useCallback(
    (basePurchaseRequest: PurchaseRequest, googleCaptchaToken: string | undefined) => {
      if (!user) {
        return {
          checkoutMutation: guestPurchaseMutation,
          purchaseRequest: {
            ...basePurchaseRequest,
            guestCheckout: true,
            email,
            googleCaptchaToken,
          } as GuestPurchaseRequest,
        }
      } else {
        return {
          checkoutMutation: purchaseMutation,
          purchaseRequest: {
            ...basePurchaseRequest,
          },
        }
      }
    },
    [email, guestPurchaseMutation, purchaseMutation, user],
  )

  const trackOrderCompleted = useCallback(
    ({ orderId, paymentMethod }: OrderCompleteArgs) => {
      const checkoutId = (getCookie('pif_checkout_id') ?? uuid()) as string
      const trackPayload: OrderCompletedPayload = {
        funnel: 'pif',
        category: 'pif',
        currency: offer.currency as MoneyCurrencyCode,
        email,
        region: offer.region,
        project: projectSlug,
        products: [
          {
            product_id: offer.id,
            sku: guildMembershipIncluded ? `${offer.slug}-guild` : offer.slug,
            category: 'pif',
            name: offer.name,
            price: getPriceForCurrency(offer.price, offer.currency),
            currency: offer.currency as MoneyCurrencyCode,
            region: offer.region,
            quantity: 1,
            url: `${paths.getBaseURL()}${paths.payItForward.index}/${projectSlug}/checkout/${offer.id}`,
          },
        ],
        subscription: false,
        checkout_id: checkoutId,
        order_id: orderId,
        payment_method: paymentMethod,
        revenue: getPriceForCurrency(offer.price, offer.currency),
        shipping: 0,
        tax: 0,
        total: getPriceForCurrency(offer.price, offer.currency),
      }

      trackOrderCompletedEvent(trackPayload)
      track(`Order Completed ${projectSlug}`, trackPayload)
    },
    [
      email,
      offer.region,
      offer.currency,
      offer.id,
      offer.name,
      offer.price,
      offer.slug,
      projectSlug,
      track,
      trackOrderCompletedEvent,
      guildMembershipIncluded,
    ],
  )

  const onPayItForwardComplete = useCallback(
    ({ orderId, paymentMethod, token, email }: OrderCompleteArgs) => {
      trackOrderCompleted({ orderId, paymentMethod })
      onComplete?.({ orderId, token, email, paymentMethod })
      // Submit Name in Credits (silent, best-effort) with basic validation
      const sanitized = sanitizeCreditName(nameInCreditsRequested)
      const lengthOk = sanitized.length >= 3 && sanitized.length <= 40
      // Only allow submission if the purchased offer meets the Name-in-Credits threshold
      const ticketCount = getPeopleImpacted(offer)
      const meetsThreshold = ticketCount >= PIF_NAME_IN_CREDITS_MIN_TICKETS
      const emailToSubmit = email || user?.email
      const canSubmit = Boolean(projectSlug) && Boolean(orderId) && lengthOk && meetsThreshold
      if (canSubmit) {
        submitNameForCredits({
          variables: {
            input: {
              orderId,
              projectSlug: projectSlug as string,
              requestedName: sanitized,
              includeInTheatricalCredits: true,
              ...(emailToSubmit && { email: emailToSubmit }),
            },
          },
        })
          .then((res) => {
            if (res?.data?.submitNameForCredits?.nameInCredits) {
              logger().info('submitNameForCredits success', {
                orderId,
                projectSlug,
                requestedNameLength: sanitized.length,
              })
            }
          })
          .catch((e) => logger().error('submitNameForCredits failed', e))
      }
    },
    [
      onComplete,
      trackOrderCompleted,
      nameInCreditsRequested,
      projectSlug,
      submitNameForCredits,
      sanitizeCreditName,
      offer,
      user?.email,
    ],
  )

  const checkout = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (isSubmitting) return

      const formError = getFormErrorMessage({ name, email })
      setErrorMessage(formError)
      if (formError) return

      setSubmitting(true)

      // skip in unit test since it's a bot testing
      let paymentMethod
      const checkoutId = (getCookie('pif_checkout_id') ?? uuid()) as string

      try {
        const googleCaptchaToken = await generateRecaptchaToken('checkout')
        paymentMethod = await getPaymentMethod({ savedPaymentInfo, elements, stripe, name })

        if (paymentMethod.errorMessage) {
          handleError({ checkoutId, errorMessage: paymentMethod.errorMessage })
          logger().error('payment method error', paymentMethod.errorMessage)
        } else {
          const { checkoutMutation, purchaseRequest } = prepareForCheckout(
            {
              offerSlug: guildMembershipIncluded ? `${offer.slug}-guild` : offer.slug,
              stripeToken: paymentMethod.stripeToken,
            },
            googleCaptchaToken,
          )

          const resp = await checkoutMutation({
            variables: { input: purchaseRequest },
          })

          if (resp.error) {
            logger().error('An error occurred during PIF checkout', {}, resp.error)
            handleError({ checkoutId })
            return
          }

          if (resp.data) {
            onPayItForwardComplete(resolvePifMutationResponse(resp.data, paymentMethod))
          }

          deleteCookie('pif_checkout_id')
        }
      } catch (e) {
        logger().error('An error occurred dureing PIF checkout', e)
        handleError({ checkoutId })
      }
    },
    [
      isSubmitting,
      name,
      email,
      savedPaymentInfo,
      elements,
      stripe,
      handleError,
      prepareForCheckout,
      offer.slug,
      onPayItForwardComplete,
      guildMembershipIncluded,
    ],
  )

  const value = useMemo(
    () => ({
      name,
      setName,
      email,
      setEmail,
      errorMessage,
      isSubmitting,
      setSubmitting,
      savedPaymentInfo,
      setSavedPaymentInfo,
      checkout,
      onPayItForwardComplete,
      setErrorMessage,
      bimClient,
      guildMembershipIncluded,
      nameInCreditsRequested,
    }),
    [
      name,
      setName,
      email,
      setEmail,
      errorMessage,
      isSubmitting,
      setSubmitting,
      savedPaymentInfo,
      setSavedPaymentInfo,
      checkout,
      onPayItForwardComplete,
      setErrorMessage,
      bimClient,
      guildMembershipIncluded,
      nameInCreditsRequested,
    ],
  )

  return <PayItForwardFormContext.Provider value={value}>{children}</PayItForwardFormContext.Provider>
}

export const usePayItForwardForm = () => {
  return React.useContext(PayItForwardFormContext)
}

function resolvePifMutationResponse(
  data: DoStripeOneTimePurchaseMutation | DoStripeMonthlySubscriptionMutation | DoStripeOneTimeGuestCheckoutMutation,
  paymentMethod: PaymentMethod,
) {
  let orderId = ''
  let userToken: string | null | undefined
  let userEmail: string | null | undefined

  if ('stripePurchase' in data) {
    orderId = data?.stripePurchase?.order?.id ?? ''
    userEmail = data?.stripePurchase?.order?.user?.email
  }

  if ('stripeSubscription' in data) {
    orderId = data?.stripeSubscription?.subscription?.id ?? ''
  }

  if ('guestStripePurchase' in data) {
    orderId = data?.guestStripePurchase?.order?.id ?? ''
    const isGuestCheckout = data?.guestStripePurchase?.userCreatedWithOrder
    userToken = isGuestCheckout ? data?.guestStripePurchase?.userToken : undefined
    userEmail = data?.guestStripePurchase?.order?.user?.email
  }

  return { orderId, paymentMethod: paymentMethod.brand ?? '', token: userToken, email: userEmail }
}
